import { useCallback, useMemo, useState } from 'react';
import { Table, TypographyWeights } from '@carboledger/ui';
import { CellText, SummaryTiles } from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { useGetMassBalanceSummaryQuery } from '../api/inventoryApi';
import { buildSummaryEmptyState, buildSummaryNoResults } from '../constants/inventoryEmptyStates';
import { buildSummaryFilters } from '../constants/summaryFilters';
import { buildMassBalanceColumns } from '../constants/summaryColumns';
import { MASS_BALANCE_FIGURES } from '../mappers/inventoryReportMappers';
import { buildMassBalanceView } from '../utils/summaryReports';

const getRowId = (row) => row.id;

const EMPTY_STATE_KEYS = {
  titleKey: 'INVENTORY_MASS_BALANCE_EMPTY_TITLE',
  descriptionKey: 'INVENTORY_MASS_BALANCE_EMPTY_DESCRIPTION',
};

/**
 * Props:
 *   header     the sub-tab strip
 *   dateRange  the From – To range, drawn first in the table's toolbar
 *   filtered   the range differs from the default, so an empty result is "no results"
 *   args       the report's variables; null while the range is invalid
 *   unit       the declaration unit symbol every figure is in
 */
export const MassBalanceSummaryView = ({ header, dateRange, filtered, args, unit }) => {
  const t = useTranslations();
  const [filters, setFilters] = useState({});
  const { data: report, isFetching } = useGetMassBalanceSummaryQuery(args, { skip: !args });

  const view = useMemo(() => buildMassBalanceView(report, filters), [report, filters]);

  const columns = useMemo(() => buildMassBalanceColumns(t, { unit }), [t, unit]);
  const filterDefs = useMemo(() => buildSummaryFilters(t, report?.rows), [t, report]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  const emptyState = useMemo(() => buildSummaryEmptyState(t, EMPTY_STATE_KEYS), [t]);
  const noResults = useMemo(() => buildSummaryNoResults(t), [t]);
  const handleFilterChange = useCallback((values) => setFilters(values), []);

  // Figma draws no tiles above an empty report — just the strip and the card.
  const tiles = useMemo(
    () =>
      view.rows.length === 0
        ? undefined
        : [
            { label: t('INVENTORY_TILE_OPENING_BALANCE'), value: view.totals.openingBalance },
            {
              label: t('INVENTORY_TILE_INBOUND_QUANTITY'),
              value: view.totals.inboundQty,
              info: t('INVENTORY_TILE_INBOUND_QUANTITY_INFO'),
            },
            { label: t('INVENTORY_TILE_ALLOCATED_QUANTITY'), value: view.totals.allocatedQty },
            { label: t('INVENTORY_TILE_CLOSING_BALANCE'), value: view.totals.closingBalance },
          ],
    [t, view],
  );

  const footerLabel = useMemo(
    () => <CellText weight={TypographyWeights.semibold}>{t('INVENTORY_TOTAL_ROW')}</CellText>,
    [t],
  );
  const footerRow = useMemo(
    () =>
      Object.fromEntries(
        MASS_BALANCE_FIGURES.map((key) => [
          key,
          <CellText key={key} weight={TypographyWeights.semibold}>
            {view.totals[key]}
          </CellText>,
        ]),
      ),
    [view.totals],
  );

  return (
    <SummaryTiles header={header} tiles={tiles}>
      <Table
        columns={columns}
        data={view.rows}
        loading={isFetching}
        getRowId={getRowId}
        footerRow={footerRow}
        footerLabel={footerLabel}
        filters={filterDefs}
        onFilterChange={handleFilterChange}
        toolbarLeft={dateRange}
        filtered={filtered}
        showPagination={false}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />
    </SummaryTiles>
  );
};
